import React, { useState } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { makeStyles } from '@material-ui/styles';
import { CheckCircle } from '@material-ui/icons';

const MILLISECONDS_PER_SECOND = 1000;
const SECONDS_TILL_REDIRECT = 5;

/**
 * Sanitizes path by making sure it is in list of the
 * allowedUrls, else returns '/'
 *
 * @param {string} url - The URL to redirect to.
 * @returns {void}
 */
export const sanitizeRedirectPath = url => {
  // We use a hardcoded string to keep snyk from flagging a potential open redirect vulnerability.
  const allowedUrls = ['/', '/home', '/administration'];
  const safeUrl = allowedUrls.find(allowed => allowed === url);

  if (safeUrl) return safeUrl;
  return '/';
};

const useStyles = makeStyles({
  background: {
    position: 'absolute',
    top: '0',
    right: '0',
    bottom: '0',
    left: '0',
    backgroundColor: 'black',
  },
  verifiedBox: {
    'margin': '0',
    'position': 'absolute',
    'top': '50%',
    'left': '50%',
    '-ms-transform': 'translate(-50%, -50%)',
    'transform': 'translate(-50%, -50%)',
    'height': '60%',
    'width': '18%',
    'borderRadius': '10px',
    'backgroundColor': 'white',
    'textAlign': 'center',
    'padding': '3%',
    'paddingTop': '4%',
  },
  dots: {
    fontSize: '50px',
    position: 'relative',
    top: '-40px',
  },
  message: {
    fontSize: '20px',
  },
  heading: {
    fontSize: '30px',
  },
  checkCircle: {
    fontSize: '90px',
    color: '#28a745',
  },
});

/**
 * React component that handles redirection to a different page upon successful completion of user email
 * verification upon signup. It uses a countdown timer to delay the redirection.
 *
 * @param {Object} props.location - Location object provided by React Router that contains information about the current URL.
 * @returns {JSX.Element} A JSX element that displays a success message and the remaining seconds before redirection.
 */
export const SuccessRedirect = ({ location }) => {
  const classes = useStyles();
  const { redirectUrl } = queryString.parse(location.search);
  const [secondsRemaining, setSecondsRemaining] = useState(SECONDS_TILL_REDIRECT);
  const sanitizedUrl = sanitizeRedirectPath(redirectUrl);

  setTimeout(
    () =>
      secondsRemaining > 1
        ? setSecondsRemaining(secondsRemaining - 1)
        : window.location.replace(sanitizedUrl),
    MILLISECONDS_PER_SECOND
  );
  return (
    <div className={classes.background}>
      <div className={classes.verifiedBox}>
        <CheckCircle className={classes.checkCircle} />
        <h1 className={classes.heading}>Success!</h1>
        <p className={classes.message}>
          You will be redirected to the application in {secondsRemaining} seconds
        </p>
        <p className={classes.dots}>
          {[...Array(SECONDS_TILL_REDIRECT - secondsRemaining)].map(() => '.').join('')}
        </p>
      </div>
    </div>
  );
};

SuccessRedirect.defaultProps = {
  location: {},
};

SuccessRedirect.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
};

export default SuccessRedirect;
